import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject } from 'rxjs'
import { filter } from 'rxjs/operators'
import * as L from 'leaflet'
import { LatLng, LatLngBounds } from 'leaflet'

import { RouteCoordinate, Vehicle, VehicleDriver, VehicleMarker, VehicleRoute } from '@shared/models/vehicle.model'
import { DeviceStatus, TrackerData } from '@shared/models/device.model'
import { Coordinate } from '@shared/models/coordinate.model'
import { Geofence, GeofenceGroup, GeofenceTruncated } from '@app/modules/client/shared/models/geofence.model'
import { StopMarker } from './shared/models/stop-marker.model'
import { DatepickerRange } from '@shared/models/datepicker-range.model'
import { VehicleView } from '@app/modules/client/map/shared/models/map.type'
import { VehicleAsset } from '@shared/models/asset.model'
import { VehicleDisplaySettings } from '@shared/models/setting.model'
import { Dayjs } from 'dayjs'
import type { Socket } from 'socket.io-client'
import type { DefaultEventsMap } from '@socket.io/component-emitter'

@Injectable()
export class MapService {
  private socket: Socket<DefaultEventsMap, DefaultEventsMap>
  constructor(private router: Router) {}

  private isLoading = new BehaviorSubject<boolean>(false)
  readonly isLoading$ = this.isLoading.asObservable()

  private isFitBounds = new BehaviorSubject<boolean>(true)
  readonly isFitBounds$ = this.isFitBounds.asObservable()

  private vehicles = new BehaviorSubject<Vehicle[] | VehicleDriver[] | VehicleAsset[]>(null)
  readonly vehicles$ = this.vehicles.asObservable()

  private vehicleWithHover = new BehaviorSubject<Vehicle>(null)
  readonly vehicleWithHover$ = this.vehicleWithHover.asObservable()

  private selectedVehicleScrollIntoList = new BehaviorSubject<VehicleMarker>(null)
  readonly selectedVehicleScrollIntoList$ = this.selectedVehicleScrollIntoList.asObservable().pipe(filter(Boolean))

  private boundsMap = new BehaviorSubject<google.maps.LatLngBounds | LatLngBounds>(null)
  readonly boundsMap$ = this.boundsMap.asObservable()

  private isSearchByBoundsMap = new BehaviorSubject<boolean>(false)
  readonly isSearchByBoundsMap$ = this.isSearchByBoundsMap.asObservable()

  private selectedDeviceToCenterMap = new BehaviorSubject<TrackerData>(null)
  readonly selectedDeviceToCenterMap$ = this.selectedDeviceToCenterMap.asObservable()

  private selectedVehicle = new BehaviorSubject<Vehicle | VehicleDriver | VehicleAsset | null>(null)
  readonly selectedVehicle$ = this.selectedVehicle.asObservable()

  private changedVehicle = new BehaviorSubject<Vehicle>(null)
  readonly changedVehicle$ = this.changedVehicle.asObservable().pipe(filter(Boolean))

  private vehicleRoutes = new BehaviorSubject<VehicleRoute[] | null>(null)
  readonly vehicleRoutes$ = this.vehicleRoutes.asObservable()

  private lastRouteCoordinates = new BehaviorSubject<Coordinate[]>(null)
  readonly lastRouteCoordinates$ = this.lastRouteCoordinates.asObservable().pipe(filter(Boolean))

  private routesCoordinates = new BehaviorSubject<RouteCoordinate[][] | null>(null)
  readonly routesCoordinates$ = this.routesCoordinates.asObservable()

  private areRoutesShown = new BehaviorSubject<boolean>(null)
  readonly areRoutesShown$ = this.areRoutesShown.asObservable()

  private isGraphShown = new BehaviorSubject<boolean>(null)
  readonly isGraphShown$ = this.isGraphShown.asObservable()

  private currentRouteCoordinates = new BehaviorSubject<Coordinate[] | null>(null)
  readonly currentRouteCoordinates$ = this.currentRouteCoordinates.asObservable()

  private areVehiclesHidden = new BehaviorSubject<boolean>(false)
  readonly areVehiclesHidden$ = this.areVehiclesHidden.asObservable()

  private selectedStopMarker = new BehaviorSubject<StopMarker>(null)
  readonly selectedStopMarker$ = this.selectedStopMarker.asObservable()

  private geofence = new BehaviorSubject<GeofenceTruncated>(null)
  readonly geofence$ = this.geofence.asObservable()

  private geofences = new BehaviorSubject<Geofence[]>(null)
  readonly geofences$ = this.geofences.asObservable().pipe(filter(Boolean))

  private geofencesGroup = new BehaviorSubject<GeofenceGroup[]>(null)
  readonly geofencesGroup$ = this.geofencesGroup.asObservable().pipe(filter(Boolean))

  private routeDateRange = new BehaviorSubject<DatepickerRange<Dayjs>>(null)
  readonly routeDateRange$ = this.routeDateRange.asObservable().pipe(filter<DatepickerRange<Dayjs>>(Boolean))

  private selectedChartPoint = new BehaviorSubject<Partial<RouteCoordinate>>(null)
  readonly selectedChartPoint$ = this.selectedChartPoint.asObservable().pipe(filter(Boolean))

  private selectedRoutePoint = new BehaviorSubject<Partial<RouteCoordinate>>(null)
  readonly selectedRoutePoint$ = this.selectedRoutePoint.asObservable().pipe(filter(Boolean))

  private selectedMapPoint = new BehaviorSubject<Partial<RouteCoordinate>>(null)
  readonly selectedMapPoint$ = this.selectedMapPoint.asObservable().pipe(filter(Boolean))

  private markerType = new BehaviorSubject<VehicleView>(null)
  readonly markerType$ = this.markerType.asObservable()

  private selectedFilter = new BehaviorSubject<DeviceStatus | string | null>(null)
  readonly selectedFilter$ = this.selectedFilter.asObservable()

  private vehicleDisplaySetting = new BehaviorSubject<VehicleDisplaySettings>(null)
  readonly vehicleDisplaySetting$ = this.vehicleDisplaySetting.asObservable()

  setIsLoading(loading: boolean): void {
    this.isLoading.next(loading)
  }

  setIsFitBounds(fitBounds: boolean): void {
    this.isFitBounds.next(fitBounds)
  }

  setVehicles(vehicles: Vehicle[]): void {
    this.vehicles.next(vehicles)
  }

  setVehicleWithHover(vehicle: Vehicle): void {
    this.vehicleWithHover.next(vehicle)
  }

  setSelectedVehicleScrollIntoList(vehicle: VehicleMarker): void {
    this.selectedVehicleScrollIntoList.next(vehicle)
  }

  setBoundsMap(boundsMap: google.maps.LatLngBounds | LatLngBounds): void {
    this.boundsMap.next(boundsMap)
  }

  setSearchByBoundsMap(value: boolean): void {
    this.isSearchByBoundsMap.next(value)
  }

  setSelectedDeviceToCenterMap(trackerData: TrackerData): void {
    this.selectedDeviceToCenterMap.next(trackerData)
  }

  setSelectedVehicle(vehicle: Vehicle | VehicleDriver | VehicleAsset | null): void {
    this.selectedVehicle.next(vehicle)
  }

  setVehicleRoutes(routes: VehicleRoute[] | null): void {
    this.vehicleRoutes.next(routes)
  }

  setChangedVehicle(vehicle: Vehicle) {
    this.changedVehicle.next(vehicle)
  }

  setRoutesCoordinates(coordinates: RouteCoordinate[][]): void {
    this.routesCoordinates.next(coordinates)
  }

  setAreRoutesShown(value: boolean): void {
    this.areRoutesShown.next(value)
  }

  setIsGraphShown(value: boolean): void {
    this.isGraphShown.next(value)
  }

  setCurrentRouteCoordinates(coordinates: Coordinate[] | null): void {
    this.currentRouteCoordinates.next(coordinates)
  }

  setAreVehiclesHidden(vehiclesShown: boolean): void {
    this.areVehiclesHidden.next(vehiclesShown)
  }

  setSelectedStopMarker(stopMarker: StopMarker): void {
    this.selectedStopMarker.next(stopMarker)
  }

  setGeofence(geofence: GeofenceTruncated): void {
    this.geofence.next(geofence)
  }

  setGeofences(geofences: Geofence[]): void {
    this.geofences.next(geofences)
  }

  setGeofencesGroup(geofencesGroup: GeofenceGroup[]): void {
    this.geofencesGroup.next(geofencesGroup)
  }

  setLastRouteCoordinates(coordinates: Coordinate[]): void {
    this.lastRouteCoordinates.next(coordinates)
  }

  setRouteDateRange(dateRange: DatepickerRange<Dayjs>): void {
    this.routeDateRange.next(dateRange)
  }

  setSelectedChartPoint(coordinate: Partial<RouteCoordinate>): void {
    this.selectedChartPoint.next(coordinate)
  }

  setSelectedRoutePoint(coordinate: Partial<Coordinate>): void {
    this.selectedRoutePoint.next(coordinate)
  }

  setSelectedMapPoint(coordinate: Partial<Coordinate>): void {
    this.selectedMapPoint.next(coordinate)
  }

  setSelectedFilter(status: DeviceStatus | string): void {
    this.selectedFilter.next(status)
  }

  setVehicleDisplaySetting(settings: VehicleDisplaySettings): void {
    this.vehicleDisplaySetting.next(settings)
  }

  filterByBounds(bounds: google.maps.LatLngBounds | LatLngBounds, vehicles: (VehicleDriver | Vehicle)[]) {
    const isIncludesBoundsMap = ({ device }: Vehicle) => {
      const coordinates = device.trackerData.lastCoordinates
      const latlng = this.createLatLng(coordinates, bounds) as LatLng
      return bounds.contains(latlng)
    }

    const filtredVehicles = vehicles.filter(isIncludesBoundsMap)
    return filtredVehicles
  }

  createLatLng(coordinates: Coordinate, bounds: google.maps.LatLngBounds | LatLngBounds): google.maps.LatLng | LatLng {
    if (typeof bounds.getSouthWest().lat === 'number') {
      return new L.LatLng(coordinates.lat, coordinates.lng)
    }
    return new google.maps.LatLng(coordinates.lat, coordinates.lng)
  }

  setMarkerType(markerType: VehicleView) {
    this.markerType.next(markerType)
  }

  purgeRoutes() {
    this.setSelectedVehicle(null)
    this.setVehicleRoutes(null)
    this.setRoutesCoordinates(null)
    this.setAreRoutesShown(false)
    this.setCurrentRouteCoordinates(null)
    this.setAreVehiclesHidden(false)
    void this.router.navigate([], { replaceUrl: true })
  }
}
